import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsPhone } from 'src/lib/customHooks';
import { trackEvent } from 'src/lib/googleAnalytics';
import { getCurrentEvents } from 'src/lib/player/selectors';
import { type SponsorLogo, useSponsorModule, useStore } from 'src/lib/player/store';
import { useElementDimensions } from 'src/lib/player/utils/useElementDimensions';
import {
  centerPinned,
  fitPrefix,
  type LogoBox,
  renderedWidth,
  sizesPending,
  UNMEASURED,
  useNaturalSizes,
  useRotatingWindow,
} from 'src/lib/sponsorLogos';

const FADE_DURATION_MS = 300;
const LOGO_GAP_PX = 10; // Gap between logos (must match CSS)
const LOGO_BOX: LogoBox = { maxWidth: 300, maxHeight: 100 }; // must match CSS
const LOGO_BOX_MOBILE: LogoBox = { maxWidth: 192, maxHeight: 64 }; // must match CSS

export const SponsorLogos: FC = () => {
  const sponsorModule = useSponsorModule();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const [isVisible, setIsVisible] = useState(true);
  const [displayedLogos, setDisplayedLogos] = useState<Array<SponsorLogo>>([]);

  const logos = sponsorModule?.enabled ? sponsorModule.logos : null;
  const naturalSizes = useNaturalSizes(logos);
  const isPhone = useIsPhone();
  const pending = sizesPending(logos, naturalSizes);

  const box = isPhone ? LOGO_BOX_MOBILE : LOGO_BOX;
  const widthOf = useCallback(
    (logo: SponsorLogo) => renderedWidth(naturalSizes[logo.id] ?? UNMEASURED, box),
    [naturalSizes, box],
  );

  const fit = useCallback(
    (order: ReadonlyArray<SponsorLogo>) =>
      pending ? [] : fitPrefix(order, widthOf, LOGO_GAP_PX, containerWidth),
    [pending, widthOf, containerWidth],
  );

  const selectedLogos = useRotatingWindow(logos, fit);

  const alignment = sponsorModule?.enabled ? sponsorModule.alignment : 'left';
  const arrangedLogos = useMemo(
    () => centerPinned(selectedLogos.filter(hasImage), alignment),
    [selectedLogos, alignment],
  );

  // Handle logo changes with fade transition
  useLogoTransition({
    arrangedLogos,
    displayedLogos,
    allWidthsLoaded: !pending,
    containerWidth,
    setDisplayedLogos,
    setIsVisible,
  });

  const handleSponsorLogoClick = useCallback(() => {
    trackEvent('logo_click', { logo: 'sponsor' });
  }, []);

  if (sponsorModule == null || !sponsorModule.enabled || !logos?.some(hasImage)) {
    return <EventLogo />;
  }

  return (
    <div
      ref={containerRef}
      className="sponsor-logos-container"
      style={{
        justifyContent: sponsorModule.alignment || 'left',
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
      }}
    >
      {displayedLogos.map((logo) => (
        <a
          key={logo.id}
          href={logo.url}
          title={logo.title}
          className="sponsor-logo-button"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSponsorLogoClick}
        >
          <img src={logo.imageUrls?.logo} alt={logo.altText || logo.title || 'Sponsor logo'} />
        </a>
      ))}
    </div>
  );
};

function handleEventLogoClick() {
  trackEvent('logo_click', { logo: 'event' });
}

/** Fills the sponsor spot when the event has no sponsor logo to show. */
const EventLogo: FC = () => {
  // Like the sponsor module: the first current event that has one.
  const event = useStore((state) => getCurrentEvents(state).find((e) => e.logoUrl != null));
  if (event?.logoUrl == null) return null;

  const image = <img src={event.logoUrl} alt={event.name} />;
  return (
    <div className="sponsor-logos-container" style={{ justifyContent: 'left' }}>
      {event.eventWebsite !== '' ? (
        <a
          href={event.eventWebsite}
          title={event.name}
          className="event-logo-button"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleEventLogoClick}
        >
          {image}
        </a>
      ) : (
        <span className="event-logo-button">{image}</span>
      )}
    </div>
  );
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Handle fade transitions when logos change
 */
function useLogoTransition({
  arrangedLogos,
  displayedLogos,
  allWidthsLoaded,
  containerWidth,
  setDisplayedLogos,
  setIsVisible,
}: {
  arrangedLogos: Array<SponsorLogo>;
  displayedLogos: Array<SponsorLogo>;
  allWidthsLoaded: boolean;
  containerWidth: number;
  setDisplayedLogos: (logos: Array<SponsorLogo>) => void;
  setIsVisible: (visible: boolean) => void;
}) {
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Wait for container width and all image widths before showing
    if (!allWidthsLoaded || containerWidth <= 0) return;

    if (arrangedLogos.length === 0) {
      setDisplayedLogos([]);
      hasInitializedRef.current = false;
      return;
    }

    // First time showing logos - show immediately without fade
    if (!hasInitializedRef.current) {
      setDisplayedLogos(arrangedLogos);
      setIsVisible(true);
      hasInitializedRef.current = true;
      return;
    }

    // Check if the set of logos changed (ignore reordering)
    const sameLogos = areSameLogoSets(displayedLogos, arrangedLogos);

    if (sameLogos) {
      // Same logos, just reordered - update without fade
      setDisplayedLogos(arrangedLogos);
      return;
    }

    // Different logos - fade out, then show new ones
    setIsVisible(false);
    const timeoutId = setTimeout(() => {
      setDisplayedLogos(arrangedLogos);
      setIsVisible(true);
    }, FADE_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [
    arrangedLogos,
    displayedLogos,
    allWidthsLoaded,
    containerWidth,
    setDisplayedLogos,
    setIsVisible,
  ]);
}

// ============================================================================
// Helper Functions
// ============================================================================

function hasImage(logo: SponsorLogo): boolean {
  return logo.imageUrls?.logo != null;
}

/**
 * Check if two logo arrays contain the same logos (ignoring order)
 */
function areSameLogoSets(logosA: Array<SponsorLogo>, logosB: Array<SponsorLogo>): boolean {
  if (logosA.length !== logosB.length) return false;
  const idsA = new Set(logosA.map((l) => l.id));
  return logosB.every((logo) => idsA.has(logo.id));
}
